import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ErrorState, Skeleton } from "@/design";
import { ApiError, fetchMe, api, beginSignup, idem, qk, setCsrfToken, unwrap, useAcceptInvitation, useInvitePreview, useMe, useSignupConfig, type LoginResponse, type PasskeyChallenge, type SignupBeginRequest } from "@/lib/api";
import { useStandaloneTitle } from "@/routes/-shared/StandaloneFrame";
import { CodeStep } from "./CodeStep";
import { EmailStep } from "./EmailStep";
import { OnboardingFrame } from "./OnboardingFrame";
import { PasskeyStep } from "./PasskeyStep";
import { PlanStep } from "./PlanStep";
import { Waitlist } from "./Waitlist";
import { WelcomeStep } from "./WelcomeStep";
import { WorkspaceStep } from "./WorkspaceStep";

export type SignupStep = "email" | "code" | "passkey" | "workspace" | "plan" | "welcome";

export interface SignupSearch {
  step?: SignupStep;
  /** Sign-up invitation code (from the waitlist), needed while sign-ups are invite-only. */
  invite?: string;
  /** Workspace invitation token: the flow ends by joining that workspace instead of creating one. */
  invitation?: string;
  /** Workspace slug created in this flow (plan and welcome steps). */
  ws?: string;
  /** Prefill, e.g. from an invitation or the marketing site. */
  email?: string;
}

/** What the browser remembers between the first three steps. Never stored: it includes the emailed code. */
export interface SignupDraft {
  name: string;
  email: string;
  verificationId: string | null;
  /** The sign-up request, kept so "Send a new code" repeats it exactly. */
  request: SignupBeginRequest | null;
  code: string;
  challenge: PasskeyChallenge | null;
  challengeAt: number;
  /** A workspace the server made for us at sign-up, which the workspace step renames instead of duplicating. */
  autoWorkspace: string | null;
}

const PROGRESS: Record<SignupStep, number | null> = { email: 0, code: 1, passkey: 2, workspace: 3, plan: 4, welcome: null };
const TITLES: Record<SignupStep, string> = {
  email: "Create your account",
  code: "Check your email",
  passkey: "Create a passkey",
  workspace: "Name your workspace",
  plan: "Choose a plan",
  welcome: "Welcome to Guv",
};

/** A short label for the passkey, so people can tell their devices apart in settings later. */
export function deviceLabel(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/CrOS/.test(ua)) return "Chromebook";
  if (/Linux/.test(ua)) return "Linux computer";
  return "This device";
}

/**
 * Sign-up: email → 6-digit code → passkey → name the workspace → choose a plan → welcome.
 * With `?invite=` it is email → code → passkey → join the workspace you were invited to.
 * The step lives in the URL so Back works; the draft (email, code, challenge) lives in memory only.
 */
export function SignupFlow() {
  const search = useSearch({ from: "/signup" });
  const navigate = useNavigate({ from: "/signup" });
  const qc = useQueryClient();
  const step: SignupStep = search.step ?? "email";
  const config = useSignupConfig();
  const me = useMe();
  const invite = useInvitePreview(search.invite ?? "");
  const accept = useAcceptInvitation();
  const [draft, setDraft] = useState<SignupDraft>({
    name: "",
    email: search.email ?? "",
    verificationId: null,
    request: null,
    code: "",
    challenge: null,
    challengeAt: 0,
    autoWorkspace: null,
  });
  const update = (patch: Partial<SignupDraft>) => setDraft((d) => ({ ...d, ...patch }));
  useStandaloneTitle(TITLES[step]);

  const go = (next: SignupStep, extra: Partial<SignupSearch> = {}, replace = false) =>
    void navigate({ search: (s: SignupSearch) => ({ ...s, step: next, ...extra }), replace });

  // Steps that need earlier answers send you back rather than half-working.
  const signedIn = me.isSuccess && !!me.data.user;
  const signedOut = me.isError && me.error instanceof ApiError && me.error.status === 401;
  useEffect(() => {
    if ((step === "code" || step === "passkey") && !draft.verificationId) go("email", {}, true);
    else if ((step === "workspace" || step === "plan" || step === "welcome") && signedOut) go("email", {}, true);
    else if ((step === "plan" || step === "welcome") && !search.ws) go("workspace", {}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, draft.verificationId, signedOut, search.ws]);

  async function signedUp(res: LoginResponse) {
    setCsrfToken(res.csrf_token);
    await qc.invalidateQueries({ queryKey: qk.me() });
    const fresh = await qc.fetchQuery({ queryKey: qk.me(), queryFn: fetchMe }).catch(() => null);
    if (search.invitation) {
      const member = await accept.mutateAsync(search.invitation);
      const joined = await qc.fetchQuery({ queryKey: qk.me(), queryFn: fetchMe }).catch(() => fresh);
      const ws = joined?.workspaces.find((w) => w.id === member.workspace_id);
      if (ws) void navigate({ to: "/$workspace", params: { workspace: ws.slug }, replace: true });
      else void navigate({ to: "/", replace: true });
      return;
    }
    const owned = fresh?.workspaces.filter((w) => w.role === "owner") ?? [];
    update({ autoWorkspace: res.new_user && owned.length === 1 ? owned[0]!.slug : null, code: "" });
    go("workspace", {}, true);
  }

  async function beginPasskey(code: string): Promise<PasskeyChallenge> {
    const ch = await unwrap(
      api.POST("/v1/auth/passkeys/register/begin", {
        params: { header: idem() },
        body: { email: draft.email, display_name: draft.name.trim() || undefined, verification_id: draft.verificationId ?? undefined, code, name: deviceLabel() },
      }),
    );
    update({ challenge: ch, challengeAt: Date.now(), code });
    return ch;
  }

  // Loading and failure of the sign-up config gate the first three steps only.
  const gated = step === "email" || step === "code" || step === "passkey";
  if (gated && config.isPending) {
    return (
      <OnboardingFrame step={PROGRESS[step]}>
        <div className="space-y-4" aria-busy="true" aria-label="Loading">
          <Skeleton className="h-9 w-3/4" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="mt-8 h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </OnboardingFrame>
    );
  }
  if (gated && config.isError) {
    return (
      <OnboardingFrame step={null}>
        <ErrorState error={config.error} title="We could not reach Guv" onRetry={() => void config.refetch()} />
      </OnboardingFrame>
    );
  }
  const mode = config.data?.mode ?? "open";
  // A workspace invitation lets you try even when sign-ups are invite-only; the server has the final say.
  if (gated && (mode === "closed" || (mode !== "open" && !search.invite && !search.invitation))) {
    return <Waitlist config={config.data!} email={draft.email} />;
  }

  switch (step) {
    case "email":
      return (
        <OnboardingFrame step={0}>
          <EmailStep
            draft={draft}
            update={update}
            config={config.data!}
            invite={search.invite ? { code: search.invite, preview: invite } : null}
            joining={!!search.invitation}
            signedInAs={signedIn && !search.invitation ? (me.data.user?.email ?? null) : null}
            onSent={() => go("code")}
            onNewWorkspace={() => go("workspace")}
          />
        </OnboardingFrame>
      );
    case "code":
      return (
        <OnboardingFrame step={1}>
          <CodeStep
            draft={draft}
            update={update}
            resend={async () =>
              (
                await beginSignup(
                  draft.request ?? { email: draft.email, invite_code: search.invite, accept_terms: true, terms_version: config.data?.terms.version ?? "", privacy_version: config.data?.privacy.version ?? "" },
                  { legacy: config.data?.legacy, powBits: config.data?.proof_of_work.difficulty_bits ?? 0 },
                )
              ).verification_id
            }
            verify={beginPasskey} onVerified={() => go("passkey")} onChangeEmail={() => go("email")} />
        </OnboardingFrame>
      );
    case "passkey":
      return (
        <OnboardingFrame step={2}>
          <PasskeyStep draft={draft} renew={() => beginPasskey(draft.code)} onDone={signedUp} onBadCode={() => go("code", {}, true)} joining={!!search.invitation} />
        </OnboardingFrame>
      );
    case "workspace":
      return (
        <OnboardingFrame step={3}>
          {me.isPending ? <Skeleton className="h-64 w-full" /> : <WorkspaceStep me={me.data ?? null} renameSlug={draft.autoWorkspace} onCreated={(slug) => go("plan", { ws: slug })} />}
        </OnboardingFrame>
      );
    case "plan":
      return (
        <OnboardingFrame step={4} wide>
          <PlanStep workspace={search.ws ?? ""} billingEnabled={config.data?.billing_enabled ?? true} onChosen={() => go("welcome")} />
        </OnboardingFrame>
      );
    case "welcome":
      return (
        <OnboardingFrame step={null} wide panel={<WelcomePanel />}>
          <WelcomeStep workspace={search.ws ?? ""} name={me.data?.user?.name ?? null} />
        </OnboardingFrame>
      );
  }
}

function WelcomePanel() {
  return (
    <>
      <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white">You're in.</h2>
      <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/70">Your workspace is ready. Most people have their first app live within three minutes of this page.</p>
    </>
  );
}
